import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 15 — Salsa Piano Tresillo.
 *
 * The Tresillo (hits on 1, the "and" of 2, and 4) deployed four ways, from
 * Jason's chart in C minor: chord hits over a root–fifth pulse, a melody on the
 * first five notes / minor pentatonic, the 3-3-2 arpeggio, and the tresillo in
 * the bass on a spread voicing (root, 5th, 10th). Slow / Fast pulse toggle:
 * LH on 1 & 3 (slow) or every beat (fast).
 */

type App = "hits" | "melody" | "arpeggio" | "bass";
type Pulse = "slow" | "fast";

const APP_INFO: Record<App, { label: string; description: string; color: string }> = {
  hits:     { label: "Chord hits",  description: "RH stamps the chord on each Tresillo cell. LH pulses underneath.", color: "amber"  },
  melody:   { label: "Melody",      description: "RH walks the minor pentatonic, three notes per bar, on the Tresillo hits.", color: "cyan"   },
  arpeggio: { label: "Arpeggio",    description: "RH plays the 3-3-2 arpeggio: low-middle-high, low-middle-high, low-middle.", color: "emerald"},
  bass:     { label: "Bass-Tresillo",description: "LH plays the Tresillo on a spread chord (root, 5th, 10th). RH keeps a slow pulse.", color: "rose"   },
};

// 8 cells per bar. Tresillo = hits on cells 0, 3, 6 (beat 1, & of 2, beat 4)
const TRESILLO_CELLS = [true, false, false, true, false, false, true, false];

export default function PianoIntermediate15() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "15");
  return (
    <LessonShell course={course} courseSlug="piano-intermediate" lessonOrder={lesson.order} title={lesson.title} description={lesson.desc} videoUrl={lesson.video} thumbUrl={lesson.thumb} accent={accent}>
      <KeyProvider defaultTonic="C" availableQualities={["natural-minor", "harmonic-minor"]}>
        <PIShell title="Salsa Piano — Tresillo" exerciseGoal="One rhythm, four ways to use it at the piano — chord hits, melody, arpeggio and bass — over a slow or a full pulse." bpm={104} difficulty="Intermediate">
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [app, setApp] = useState<App>("hits");
  const [pulse, setPulse] = useState<Pulse>("slow");
  const [bpm, setBpm] = useState(104);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  const stop = () => { handleRef.current?.stop(); handleRef.current = null; setRunning(false); setStep(-1); };

  // Live tempo — ramp the Transport BPM whenever the slider moves during playback
  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);
    // 8 cells per bar × 2 bars = 16 cells. Each cell is an EIGHTH note → subdivide=2.
    const totalSteps = 16;
    const events: { beat: number; notes: string[]; durationSteps: number; velocity?: number }[] = [];
    const minor3 = 3; // we're in minor key
    const chord = [tonicMidi, tonicMidi + minor3, tonicMidi + 7];
    const bassRoot = tonicMidi - 12;
    const bassFifth = tonicMidi - 12 + 7;

    for (let bar = 0; bar < 2; bar++) {
      const cellOffset = bar * 8;

      // RH: per app
      if (app === "hits") {
        TRESILLO_CELLS.forEach((hit, j) => {
          if (hit) events.push({ beat: cellOffset + j, notes: chord.map((m) => midiToPitchName(m, k.useFlats)), durationSteps: 1, velocity: 0.9 });
        });
      } else if (app === "melody") {
        // Jason's chart, minor pentatonic: C-E♭-F | G-B♭-G (semitones above the tonic).
        const line = [[0, 3, 5], [7, 10, 7]][bar];
        let p = 0;
        TRESILLO_CELLS.forEach((hit, j) => {
          if (hit) {
            events.push({ beat: cellOffset + j, notes: [midiToPitchName(tonicMidi + line[p], k.useFlats)], durationSteps: j === 6 ? 2 : j === 0 ? 3 : 1, velocity: 0.85 });
            p += 1;
          }
        });
      } else if (app === "arpeggio") {
        // 3-3-2 arpeggio: L-M-H, L-M-H, L-M on all eight eighths; the low note marks the hits.
        for (let j = 0; j < 8; j++) {
          events.push({ beat: cellOffset + j, notes: [midiToPitchName(chord[j % 3], k.useFlats)], durationSteps: 1, velocity: TRESILLO_CELLS[j] ? 0.9 : 0.5 });
        }
      } else {
        // bass-Tresillo: LH plays the Tresillo on Jason's spread voicing — root,
        // fifth and the (minor) tenth; RH keeps a slow pulse on 1 & 3.
        TRESILLO_CELLS.forEach((hit, j) => {
          if (hit) events.push({
            beat: cellOffset + j,
            notes: [bassRoot, bassFifth, bassRoot + 15].map((m) => midiToPitchName(m, k.useFlats)),
            durationSteps: j === 6 ? 2 : j === 0 ? 3 : 1, velocity: 0.85
          });
        });
        // RH chord stabs at beats 1 and 3 (cells 0 and 4) — staccato
        events.push({ beat: cellOffset,     notes: chord.map((m) => midiToPitchName(m, k.useFlats)), durationSteps: 1, velocity: 0.7 });
        events.push({ beat: cellOffset + 4, notes: chord.map((m) => midiToPitchName(m, k.useFlats)), durationSteps: 1, velocity: 0.7 });
      }

      // LH: pulse (only when app !== "bass" because bass already takes LH)
      if (app !== "bass") {
        if (pulse === "slow") {
          // Hits on beats 1 and 3 only — cells 0 and 4
          // Jason's chart: root on beat 1, fifth on beat 3 (half notes).
          events.push({ beat: cellOffset,     notes: [midiToPitchName(bassRoot, k.useFlats)], durationSteps: 4, velocity: 0.75 });
          events.push({ beat: cellOffset + 4, notes: [midiToPitchName(bassFifth, k.useFlats)], durationSteps: 4, velocity: 0.7 });
        } else {
          // Fast pulse: hits on every beat — cells 0, 2, 4, 6
          [0, 2, 4, 6].forEach((cellIdx, n) => {
            events.push({ beat: cellOffset + cellIdx, notes: [midiToPitchName(n % 2 === 0 ? bassRoot : bassFifth, k.useFlats)], durationSteps: 2, velocity: 0.7 });
          });
        }
      }
    }
    handleRef.current = await playRhythm(events, {
      bpm, subdivide: 2, totalSteps, loop: true, click: true, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  const masterHighlights: KeyHighlight[] = [
    { degree: 1, tone: "root", label: "1" },
    { degree: 5, tone: "voice", label: "5" },
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={masterHighlights} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Tonic and fifth are the bass anchors of every Tresillo application. Pick any minor key —
          the rhythm carries identically.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="One rhythm. Four wardrobes.">
          <p>
            The <strong>Tresillo</strong> is a one-bar, 3-3-2 grouping of 8 eighth notes: hits on
            1, the "and" of 2, and 4. Count "1 and 2 and 3 and 4 and" and land on the 1st, 4th and
            7th eighth. Jason calls it very pop-ish — over a slow pulse it's the "pop soca" vibe.
            Then there are four ways to use it on the piano.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Tresillo is really infectious. Once you get it, you don't want to stop playing it."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Pick a wardrobe" title="Each application uses the same Tresillo cells">
          <div className="my-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            {(Object.keys(APP_INFO) as App[]).map((id) => (
              <motion.button key={id} layout onClick={() => { stop(); setApp(id); }}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  app === id ? "border-amber-400/60 bg-amber-500/10" : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                }`}>
                <div className={`text-[13px] font-bold ${app === id ? "text-amber-200" : "text-cyan-100"}`}>{APP_INFO[id].label}</div>
                <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">{APP_INFO[id].description}</div>
              </motion.button>
            ))}
          </div>

          <DrumGrid app={app} pulse={pulse} step={running ? step : -1} />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={play} disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50">
              ▶ Loop in {k.tonicName}
            </button>
            {running && <button onClick={stop} className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20">■ Stop</button>}
            <div className="ml-2 inline-flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-500/5 p-0.5">
              {(["slow", "fast"] as Pulse[]).map((p) => (
                <button key={p} onClick={() => { stop(); setPulse(p); }}
                  className={`rounded-md px-3 py-1 font-mono text-[11px] font-bold uppercase ${
                    pulse === p ? "bg-amber-400 text-zinc-900" : "text-cyan-200 hover:bg-cyan-500/15"
                  }`}>
                  {p} pulse
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input type="range" min={70} max={130} step={1} value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="h-1 w-32 accent-amber-400" />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>
          <p className="mt-2 text-[12px] italic text-cyan-300/70">
            Slow pulse = LH on beats 1 and 3 only — root, then fifth (the pop-soca feel Jason
            prefers). Fast pulse = LH on every beat, root–fifth–root–fifth.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Body first" title="Train it on your chest before the keys">
          <p>
            Before the keys, tap it. Tresillo with your right hand, the slow pulse with your left —
            on your body or on a bongo. The chest gives a bassy, kick-drum sound; the leg more of a
            snare whack. Then flip it: Tresillo in the left, pulse in the right. When both feel
            locked, transfer the same coordination to the piano.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "So doing it on the chest gives you a very bassy sound, you can hear, very kick, and
            this gives you more like a whack or a snare sound."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The flip" title="Bass-Tresillo, melody-pulse">
          <p>
            The "Bass-Tresillo" option flips the geography: the LH carries the Tresillo and the
            RH is free. Jason's left-hand voicing is a spread chord — the root, the fifth and the
            tenth above (a minor tenth on a minor chord). If the stretch is too big, play the root
            alone.
          </p>
          <PIInsight>
            <p>
              With the rhythm and the harmony in the left hand, the right hand is free to
              improvise on top.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="The Tresillo melody trick" title="A pentatonic on the 3-3-2">
          <p>
            For the melody application, take the first five notes of the minor scale (1 2 ♭3 4 5)
            or the minor pentatonic (1 ♭3 4 5 ♭7) and walk them up and down, three notes per bar,
            one on each Tresillo hit. Jason's chart starts C-E♭-F, G-B♭-G in C minor. The result is
            a syncopated phrase — you can even take a rather boring finger drill and "Tresillo" it.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="Salsa Tresillo loop">
          <LessonMidi courseSlug="piano-intermediate" order="15" label={`Tresillo · ${k.tonicName}`} originalBpm={bpm} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={<>Slow pulse first; lock the rhythm. Add the wardrobe variations once your hands stop fighting each other.</>}
            rungs={[
              { title: "Hits + slow pulse",         minutes: 4, accent: "amber",   detail: "RH chord hits on the Tresillo. LH root on beat 1, fifth on beat 3." },
              { title: "Melody on the cells",        minutes: 4, accent: "cyan",    detail: "Pentatonic shape walking through the Tresillo cells. Sing along while you play." },
              { title: "Arpeggio Tresillo",          minutes: 4, accent: "rose",    detail: "Low-middle-high, low-middle-high, low-middle — the 3-3-2 arpeggio IS the Tresillo." },
              { title: "Bass-Tresillo",              minutes: 4, accent: "emerald", detail: "Flip — LH plays the Tresillo on the spread chord (or the root alone). RH keeps the pulse, then improvises." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: DrumGrid — RH (kick) lane and LH (hat) lane ── */
function DrumGrid({ app, pulse, step }: { app: App; pulse: Pulse; step: number }) {
  // 16 cells = 2 bars × 8 eighth notes
  const TOTAL = 16;
  const PER_BAR = 8;
  const appColors: Record<App, string> = {
    hits: "bg-amber-400", melody: "bg-cyan-400", arpeggio: "bg-emerald-400", bass: "bg-rose-400",
  };
  const lhColor = "bg-zinc-400";

  // RH cells per app: which cells of the bar are hit?
  const rhBarCells = (() => {
    if (app === "bass") {
      // chord stabs on beats 1 and 3 (cells 0 and 4)
      const arr = Array(PER_BAR).fill(false);
      arr[0] = true; arr[4] = true;
      return arr;
    }
    // The 3-3-2 arpeggio sounds on every eighth.
    if (app === "arpeggio") return Array(PER_BAR).fill(true);
    return TRESILLO_CELLS;
  })();

  // LH cells per app + pulse
  const lhBarCells = (() => {
    if (app === "bass") {
      // LH carries the Tresillo
      return TRESILLO_CELLS;
    }
    if (pulse === "slow") {
      const arr = Array(PER_BAR).fill(false);
      arr[0] = true; arr[4] = true; // beats 1 and 3
      return arr;
    }
    // fast: every beat
    const arr = Array(PER_BAR).fill(false);
    arr[0] = true; arr[2] = true; arr[4] = true; arr[6] = true;
    return arr;
  })();

  // Beat labels: "1 & 2 & 3 & 4 &"
  const labels = ["1", "&", "2", "&", "3", "&", "4", "&"];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ▤ Drum grid · {APP_INFO[app].label}
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          2 bars · 8 eighths each · {pulse} pulse
        </div>
      </div>

      {/* RH lane */}
      <Lane
        label={app === "bass" ? "RH chord" : `RH ${APP_INFO[app].label.toLowerCase()}`}
        labelColor="text-amber-300"
        cellsPerBar={rhBarCells}
        total={TOTAL}
        perBar={PER_BAR}
        color={appColors[app]}
        step={step}
      />

      {/* LH lane */}
      <Lane
        label={app === "bass" ? "LH Tresillo" : `LH ${pulse === "slow" ? "slow pulse (1, 3)" : "fast pulse (every beat)"}`}
        labelColor="text-cyan-300"
        cellsPerBar={lhBarCells}
        total={TOTAL}
        perBar={PER_BAR}
        color={app === "bass" ? appColors[app] : lhColor}
        step={step}
      />

      {/* Beat-position labels */}
      <div className="mt-1 grid gap-1 px-[5.5rem]" style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}>
        {[...labels, ...labels].map((lab, i) => (
          <span key={i}
            className={`text-center text-[10px] font-mono ${
              lab === "1" ? "font-bold text-amber-300" :
              i === 8 ? "font-bold text-cyan-300" :
              "text-cyan-300/50"
            }`}>
            {lab}
          </span>
        ))}
      </div>

      {/* Bar markers */}
      <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-wider text-cyan-300/70">
        <div className="rounded bg-zinc-800/40 py-0.5 text-center">Bar 1</div>
        <div className="rounded bg-zinc-800/40 py-0.5 text-center">Bar 2</div>
      </div>
    </div>
  );
}

function Lane({
  label, labelColor, cellsPerBar, total, perBar, color, step,
}: {
  label: string; labelColor: string; cellsPerBar: boolean[]; total: number; perBar: number; color: string; step: number;
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className={`w-20 shrink-0 text-[11px] font-bold uppercase tracking-wider ${labelColor}`}>
        {label}
      </span>
      <div className="grid flex-1 gap-1" style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}>
        {Array.from({ length: total }, (_, i) => {
          const cellInBar = i % perBar;
          const hit = cellsPerBar[cellInBar];
          const isStep = i === step;
          const isDownbeat = cellInBar === 0;
          return (
            <motion.div key={i}
              animate={{ scale: isStep ? 1.18 : 1, boxShadow: isStep ? "0 0 0 2px #FCD34D" : "0 0 0 0 transparent" }}
              transition={{ duration: 0.1 }}
              className={`flex h-12 items-center justify-center rounded ${
                hit ? color : "bg-zinc-800/50"
              } ${isDownbeat && hit ? "ring-1 ring-white/40" : ""}`}>
              {hit && (
                <span className="text-[10px] font-bold text-zinc-900">
                  {isDownbeat ? "▾" : "·"}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (<motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>{children}</motion.div>);
}
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>{children}</blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
